import {
	CardShell,
	SectionCard,
	SelectField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAxes, useCreateAxis } from '@/hooks/useClassification'
import { TextField } from './FormField'

/**
 * Axis registry manager — list every axis and create new ones. Rendered from the
 * live registry (no hard-coded dimension names). Adding an axis is an INSERT, so
 * a new one appears here without any code change (design D5).
 */
const EMPTY = { code: '', name: '', kind: 'dimension', valueType: 'list', isHierarchical: false }
const VALUE_TYPES = ['list', 'text', 'url', 'date', 'number']

const opt = (vals: readonly string[]) => vals.map((v) => ({ value: v, label: v }))

function CreateAxisForm({ onClose }: { onClose: () => void }) {
	const createAxis = useCreateAxis()
	const [form, setForm] = useState(EMPTY)
	const set = (k: keyof typeof EMPTY) => (v: string) => setForm({ ...form, [k]: v })
	const submit = () => {
		if (form.code && form.name) createAxis.mutate(form, { onSuccess: onClose })
	}
	return (
		<SectionCard title="Create axis" description="Code is stable and immutable after creation.">
			<div className="flex flex-wrap gap-3 items-end">
				<TextField label="Code" value={form.code} onChange={set('code')} placeholder="campaign_wave" />
				<TextField label="Name" value={form.name} onChange={set('name')} placeholder="Campaign Wave" />
				{/* Enums with a default — an "unset" option would be an invalid choice. */}
				<SelectField
					label="Kind"
					value={form.kind}
					onChange={set('kind')}
					options={opt(['dimension', 'attribute'])}
					isClearable={false}
				/>
				<SelectField
					label="Value type"
					value={form.valueType}
					onChange={set('valueType')}
					options={opt(VALUE_TYPES)}
					isClearable={false}
				/>
				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={form.isHierarchical}
						onChange={(e) => setForm({ ...form, isHierarchical: e.target.checked })}
					/>
					Hierarchical
				</label>
				<Button onPress={submit} isDisabled={createAxis.isPending}>
					{createAxis.isPending ? 'Creating…' : 'Create'}
				</Button>
				{createAxis.isError && <span className="text-sm text-ds-danger">Failed (duplicate code?)</span>}
			</div>
		</SectionCard>
	)
}

/**
 * Dimensions first, then attributes, alphabetical by name within each.
 *
 * The two kinds answer different questions — a dimension is a facet you filter by, an
 * attribute is a property a value HAS (ADR-0007 §2) — and an administrator scanning the
 * registry is looking within one kind at a time, not across both.
 */
const KIND_ORDER: Record<string, number> = { dimension: 0, attribute: 1 }

export function sortAxes<T extends { kind: string; name: string; status?: string }>(axes: readonly T[]): T[] {
	return [...axes].sort(
		(a, b) =>
			(KIND_ORDER[a.kind] ?? 2) - (KIND_ORDER[b.kind] ?? 2) ||
			a.name.localeCompare(b.name, undefined, { numeric: true }),
	)
}

function AxisTable({ axes }: { axes: ReturnType<typeof useAxes>['data'] & object }) {
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Code</TableHead>
					<TableHead>Kind</TableHead>
					<TableHead>Type</TableHead>
					<TableHead>Hierarchical</TableHead>
					<TableHead>Values</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{sortAxes(axes).map((axis) => (
					<TableRow key={axis.axisId}>
						<TableCell className="font-medium">
							{axis.name}
							{/* A retired axis stays listed — its values are still referenced by
							    everything already classified — but it must not read as live. */}
							{axis.status === 'retired' && (
								<Badge variant="muted" size="sm" className="ml-2">
									retired
								</Badge>
							)}
						</TableCell>
						<TableCell className="text-ds-text-muted">{axis.code}</TableCell>
						<TableCell>{axis.kind}</TableCell>
						<TableCell>{axis.valueType}</TableCell>
						<TableCell>{axis.isHierarchical ? 'yes' : '—'}</TableCell>
						<TableCell>
							<Link className="text-ds-info hover:underline" to={`/admin/classification/axes/${axis.axisId}`}>
								manage values →
							</Link>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

export function RegistryPage() {
	const { data: axes = [], isLoading } = useAxes()
	const [open, setOpen] = useState(false)
	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold">Axis Registry</h1>
					<p className="text-ds-text-muted">{isLoading ? 'Loading…' : `${axes.length} axes`}</p>
				</div>
				<div className="flex items-center gap-3">
					<Link className="text-sm text-ds-primary hover:underline" to="/admin/classification/full">
						Full taxonomy
					</Link>
					<Link className="text-sm text-ds-primary hover:underline" to="/admin/classification/systematic">
						Systematic view
					</Link>
					<Button onPress={() => setOpen((o) => !o)}>{open ? 'Cancel' : 'New axis'}</Button>
				</div>
			</div>
			{open && <CreateAxisForm onClose={() => setOpen(false)} />}
			<CardShell>
				<AxisTable axes={axes} />
			</CardShell>
		</div>
	)
}
